import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Bell,
  ChevronRight,
  LogOut,
  Menu,
  Monitor,
  Moon,
  PackageX,
  Plus,
  Search,
  Settings,
  Sun,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useTheme, type Theme } from '@/hooks/useTheme';
import { usePermissions } from '@/hooks/usePermissions';
import { useLowStock } from '@/hooks/useLowStock';
import { QUICK_ACTIONS, ROLE_LABELS, getBreadcrumbs } from '@/config/navigation';
import { MOD_KEY } from '@/utils/platform';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Kbd } from '@/components/ui/kbd';
import { SimpleTooltip } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/utils/cn';
import { formatNumber } from '@/utils/formatters';

interface HeaderProps {
  onOpenMobileNav: () => void;
  onOpenCommand: () => void;
}

const Breadcrumbs: React.FC = () => {
  const location = useLocation();
  const crumbs = getBreadcrumbs(location.pathname, location.search);
  if (!crumbs.length) return null;

  return (
    <nav aria-label="Навигационная цепочка" className="min-w-0">
      <ol className="flex min-w-0 items-center gap-1 text-[13px]">
        {crumbs.map((crumb, index) => {
          const isLast = index === crumbs.length - 1;
          return (
            <li
              key={`${crumb.label}-${index}`}
              className={cn('flex min-w-0 items-center gap-1', !isLast && 'hidden md:flex')}
            >
              {crumb.path && !isLast ? (
                <Link to={crumb.path} className="truncate text-muted-foreground transition-colors hover:text-foreground">
                  {crumb.label}
                </Link>
              ) : (
                <span className={cn('truncate', isLast ? 'font-medium text-foreground' : 'text-muted-foreground')}>
                  {crumb.label}
                </span>
              )}
              {!isLast && <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50" />}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

const NotificationsButton: React.FC = () => {
  const navigate = useNavigate();
  const { data } = useLowStock();
  const items = data?.data ?? [];
  const count = items.length;

  return (
    <Popover>
      <SimpleTooltip label="Уведомления">
        <PopoverTrigger asChild>
          <Button variant="ghost" size="icon-sm" className="relative text-muted-foreground hover:text-foreground" aria-label={count ? `Уведомления: ${count}` : 'Уведомления'}>
            <Bell className="h-[18px] w-[18px]" />
            {count > 0 && (
              <span className="absolute right-1 top-1 flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-warning opacity-60 motion-safe:animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-warning ring-2 ring-surface" />
              </span>
            )}
          </Button>
        </PopoverTrigger>
      </SimpleTooltip>
      <PopoverContent align="end" className="w-[22rem] p-0">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div>
            <p className="text-sm font-semibold">Уведомления</p>
            <p className="text-xs text-muted-foreground">
              {count ? `Заканчиваются: ${count} поз.` : 'Всё в порядке'}
            </p>
          </div>
        </div>
        {count === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">Новых уведомлений нет</div>
        ) : (
          <ul className="max-h-80 divide-y overflow-y-auto">
            {items.slice(0, 8).map((item) => (
              <li key={`${item.id}-${item.warehouseId}`} className="flex items-center gap-3 px-4 py-2.5">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg tone-warning ring-1 ring-inset">
                  <PackageX className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-medium">{item.productName}</p>
                  <p className="truncate text-xs text-muted-foreground">{item.warehouseName}</p>
                </div>
                <span className="shrink-0 text-xs font-semibold tabular-nums text-warning">
                  {formatNumber(Number(item.quantity), Number(item.quantity) % 1 === 0 ? 0 : 2)} {item.unitShortName}
                </span>
              </li>
            ))}
          </ul>
        )}
        {count > 0 && (
          <div className="border-t p-2">
            <Button variant="ghost" size="sm" className="w-full" onClick={() => navigate('/stock-balances')}>
              Все остатки
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

const THEME_OPTIONS: { value: Theme; label: string; icon: typeof Sun }[] = [
  { value: 'light', label: 'Светлая', icon: Sun },
  { value: 'dark', label: 'Тёмная', icon: Moon },
  { value: 'system', label: 'Как в системе', icon: Monitor },
];

export const Header: React.FC<HeaderProps> = ({ onOpenMobileNav, onOpenCommand }) => {
  const navigate = useNavigate();
  const { state, logout } = useAuth();
  const { theme, resolvedTheme, setTheme } = useTheme();
  const { canOperate, isAdmin } = usePermissions();
  const user = state.user;

  const initials = user ? `${user.firstName?.charAt(0) ?? ''}${user.lastName?.charAt(0) ?? ''}`.toUpperCase() : '';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header className="flex h-14 shrink-0 items-center gap-2 border-b bg-surface/80 px-3 backdrop-blur supports-[backdrop-filter]:bg-surface/70 sm:px-5 print:hidden">
      <Button
        variant="ghost"
        size="icon-sm"
        className="-ml-1 text-muted-foreground lg:hidden"
        onClick={onOpenMobileNav}
        aria-label="Открыть меню"
      >
        <Menu className="h-5 w-5" />
      </Button>

      <Breadcrumbs />

      <div className="ml-auto flex items-center gap-1 sm:gap-1.5">
        {/* Поиск и команды */}
        <button
          type="button"
          onClick={onOpenCommand}
          className="hidden h-8 w-56 items-center gap-2 rounded-md border border-input bg-field px-2.5 text-[13px] text-muted-foreground shadow-xs transition-colors hover:border-muted-foreground/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/35 md:flex xl:w-64"
        >
          <Search className="h-3.5 w-3.5" />
          <span className="flex-1 text-left">Поиск и команды…</span>
          <Kbd>{MOD_KEY} K</Kbd>
        </button>
        <Button
          variant="ghost"
          size="icon-sm"
          className="text-muted-foreground md:hidden"
          onClick={onOpenCommand}
          aria-label="Поиск и команды"
        >
          <Search className="h-[18px] w-[18px]" />
        </Button>

        {/* Быстрое создание */}
        {canOperate && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" className="gap-1.5 px-2.5 sm:px-3">
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">Создать</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel>Новый документ</DropdownMenuLabel>
              {QUICK_ACTIONS.map((action) => (
                <DropdownMenuItem key={action.path} onSelect={() => navigate(action.path)} className="py-2">
                  <action.icon />
                  <div className="min-w-0">
                    <div className="leading-tight">{action.label}</div>
                    <div className="truncate text-xs text-muted-foreground">{action.hint}</div>
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        <div className="mx-1 hidden h-5 w-px bg-border sm:block" />

        <NotificationsButton />

        {/* Профиль */}
        {user && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-2 rounded-full outline-none ring-offset-2 ring-offset-surface transition-shadow focus-visible:ring-[3px] focus-visible:ring-ring/35"
                aria-label="Меню профиля"
              >
                <Avatar className="h-8 w-8">
                  <AvatarFallback>{initials || '?'}</AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <div className="flex items-center gap-3 px-2 py-2">
                <Avatar className="h-9 w-9">
                  <AvatarFallback>{initials || '?'}</AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">
                    {user.firstName} {user.lastName}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">{user.email}</p>
                </div>
              </div>
              <div className="px-2 pb-2">
                <span className="inline-flex rounded-md px-1.5 py-0.5 text-[11px] font-medium tone-primary ring-1 ring-inset">
                  {ROLE_LABELS[user.role] ?? user.role}
                </span>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  {resolvedTheme === 'dark' ? <Moon /> : <Sun />}
                  Тема оформления
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="w-44">
                  <DropdownMenuRadioGroup value={theme} onValueChange={(value) => setTheme(value as Theme)}>
                    {THEME_OPTIONS.map((option) => (
                      <DropdownMenuRadioItem key={option.value} value={option.value}>
                        {option.label}
                      </DropdownMenuRadioItem>
                    ))}
                  </DropdownMenuRadioGroup>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              <DropdownMenuItem onSelect={onOpenCommand}>
                <Search />
                Поиск и команды
                <DropdownMenuShortcut>{MOD_KEY}K</DropdownMenuShortcut>
              </DropdownMenuItem>
              {isAdmin && (
                <DropdownMenuItem onSelect={() => navigate('/settings')}>
                  <Settings />
                  Настройки
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive" onSelect={handleLogout}>
                <LogOut />
                Выйти
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </header>
  );
};
